import React, { useMemo, useState, useEffect, useCallback } from "react";
import ReactTooltip from "react-tooltip";

import VerticalSpacing from "@cayuse/shared-react.components.vertical-spacing";
import Button from "@cayuse/shared-react.components.button";
import SubHeader from "@cayuse/shared-react.components.sub-header";
import Number from "@cayuse/shared-react.components.form.inputs.number";
import {
    isNonEmptyArray,
    isNonEmptyString,
    isNonEmptyMap,
} from "@cayuse/shared-react.utils.type-checks";

import { useRuleBuilderContext } from "containers/Rules/context/useRuleBuilderContext";
import { useDictionary, useResearchTeamRoles } from "hooks";
import { NEW_RULE_ID } from "globals/constants";

import { PreventLeaveDialog } from "@cayuse/shared-react.components.prevent-leave-page-dialog";
import Table from "components/Table";
import RuleRow from "./RulesRenderer/RuleRow";
import ConditionButton from "./ConditionButton/ConditionButton";

import styles from "./styles.css";

const RulesListView = () => {
    const {
        rules,
        setRules,
        formVersion,
        ruleOrderIndexes,
        setRuleOrderIndexes,
        sortRules,
        hasUnsavedOrderChanges,
        getNewRuleIndex,
        editingCondition,
        isEditingAction,
        newRuleOrder, 
        setNewRuleOrder,
    } = useRuleBuilderContext();

    const [isSavingOrder, setIsSavingOrder] = useState(false);
    const dictionary = useDictionary("ruleBuilder");

    const hasRules = useMemo(() => isNonEmptyArray(rules), [rules]);
    const newRule = useMemo(() => {
        return hasRules
            ? rules.find((rule) => rule.id === NEW_RULE_ID)
            : undefined;
    }, [hasRules, rules]);
    const hasNewRule = useMemo(() => !!newRule, [newRule]);

    const hasUnsavedChanges = useMemo(() => {
        return hasNewRule || hasUnsavedOrderChanges;
    }, [hasNewRule, hasUnsavedOrderChanges]);

    useEffect(() => {
        const orderedOriginalRules = isNonEmptyArray(rules)
            ? rules.map((rule) => {
                  const isOrdered =
                      isNonEmptyMap(ruleOrderIndexes) &&
                      ruleOrderIndexes.has(rule.id);

                  const orderIndex = isOrdered
                      ? ruleOrderIndexes.get(rule.id)
                      : rule.orderIndex;

                  return { ...rule, orderIndex };
              })
            : [...rules];

        setRules([...orderedOriginalRules]);
        setNewRuleOrder(getNewRuleIndex());
    }, [ruleOrderIndexes]);

    useResearchTeamRoles();

    const doesRuleIdHaveValidOrderIndex = useCallback(
        (ruleId) => {
            const orderIndex = isNonEmptyMap(ruleOrderIndexes)
                ? ruleOrderIndexes.get(ruleId)
                : null;

            return (
                orderIndex !== null && isFinite(orderIndex) && orderIndex > 0
            );
        },
        [ruleOrderIndexes]
    );

    const tableHeader = (
        <div className={styles.tableHeader}>
            <SubHeader>
                <h2>
                    {dictionary.tableHeading || "Review and Notification Rules"}
                </h2>
            </SubHeader>
            {formVersion > 0 && (
                <div className={styles.version}>
                    {dictionary.version || "Version"} {formVersion}
                </div>
            )}
        </div>
    );

    useEffect(() => {
        if (ruleOrderIndexes === null) {
            const orderIndexes = new Map();

            if (isNonEmptyArray(rules)) {
                rules.forEach((rule) =>
                    orderIndexes.set(rule.id, rule.orderIndex)
                );
            }

            setRuleOrderIndexes(orderIndexes);
        } else {
            const updatedRuleOrderIndexes = new Map(ruleOrderIndexes);
            let hasChanges = false;

            if (!hasNewRule && ruleOrderIndexes.has(NEW_RULE_ID)) {
                updatedRuleOrderIndexes.delete(NEW_RULE_ID);
                hasChanges = true;
            }

            rules.forEach(({ id, orderIndex }) => {
                if (!updatedRuleOrderIndexes.has(id)) {
                    updatedRuleOrderIndexes.set(id, orderIndex);
                    hasChanges = true;
                }
            });

            updatedRuleOrderIndexes.forEach((value, key) => {
                if (
                    !rules.some((rule) => rule.id === key) ||
                    rules.length === 0
                ) {
                    updatedRuleOrderIndexes.delete(key);
                    hasChanges = true;
                }
            });

            if (hasChanges) {
                setRuleOrderIndexes(updatedRuleOrderIndexes);
            }
        }
    }, [hasNewRule, ruleOrderIndexes, rules]);

    const sortDisabledReason = useMemo(() => {
        // determine if sort is disabled
        let disabledReason = "";
        const keys = isNonEmptyMap(ruleOrderIndexes)
            ? Array.from(ruleOrderIndexes.keys())
            : [];

        const hasMissingOrderIndexes =
            hasRules &&
            (!keys.length ||
                keys.some((ruleId) => !doesRuleIdHaveValidOrderIndex(ruleId)));

        if (!hasRules) {
            disabledReason =
                dictionary.errors?.noRulesToOrder ||
                "No rules have been created yet";
        } else if (!hasUnsavedOrderChanges) {
            disabledReason =
                dictionary.errors?.noOrderChanges ||
                "No unsaved review order indices";
        } else if (hasNewRule) {
            disabledReason =
                dictionary.errors?.isAddingRule ||
                "Save or cancel the new rule being created before reordering";
        } else if (hasMissingOrderIndexes) {
            disabledReason =
                dictionary.errors?.notAllRulesOrderIndexes ||
                "Add a number between 1 - 99 to all rules to reorder them";
        }

        return disabledReason;
    }, [
        hasUnsavedOrderChanges,
        hasRules,
        hasNewRule,
        ruleOrderIndexes,
        doesRuleIdHaveValidOrderIndex,
        dictionary.errors,
    ]);

    const handleSort = () => {
        setIsSavingOrder(true);
        sortRules().finally(() => setIsSavingOrder(false));
    };

    useEffect(() => {
        ReactTooltip.rebuild();
    }, [sortDisabledReason]);

    return (
        <div data-component="RulesListView">
            <VerticalSpacing>
                <Table
                    header={tableHeader}
                    overflow="visible"
                    className={styles.table}
                    captionText="Rules"
                >
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell className={styles.reviewOrderCol}>
                                {dictionary.columnHeaders?.reviewOrder ||
                                    "Review Order"}
                            </Table.HeaderCell>
                            <Table.HeaderCell className={styles.condtionHeader}>
                                {dictionary.columnHeaders?.condition ||
                                    "Condition"}
                            </Table.HeaderCell>
                            <Table.HeaderCell
                                colSpan="2"
                                className={styles.actionCol}
                            >
                                {dictionary.columnHeaders?.action || "Action"}
                            </Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>

                    <Table.Body>
                        {rules.map((rule) => (
                            <RuleRow rule={rule} hasNewRule={hasNewRule} />
                        ))}
                        {!editingCondition && !isEditingAction && (
                            <Table.Row>
                                <Table.Cell
                                    width={1}
                                    className={styles.reviewOrderCol}
                                >
                                    <Number
                                        className={styles.orderInput}
                                        key={`rule-order-add`}
                                        maxLength="2"
                                        value={newRuleOrder}
                                        onChange={(event) =>
                                            setNewRuleOrder(
                                                event.target.value
                                            )
                                        }
                                    />
                                </Table.Cell>
                                <Table.Cell
                                    width={14}
                                    colSpan="3"
                                    className={styles.conditionButton}
                                >
                                    <ConditionButton />
                                </Table.Cell>
                            </Table.Row>
                        )}
                    </Table.Body>
                </Table>
            </VerticalSpacing>

            <div className={styles.saveOrderButton}>
                <Button
                    primary
                    onClick={handleSort}
                    disabled={
                        hasNewRule ||
                        isNonEmptyString(sortDisabledReason) ||
                        editingCondition ||
                        isEditingAction
                    }
                    tooltip={((hasNewRule || editingCondition || isEditingAction) && isNonEmptyString(sortDisabledReason)) && sortDisabledReason}
                    busy={isSavingOrder}
                    type="button"
                >
                    {dictionary.saveListOrder || "Save List Order"}
                </Button>
            </div>

            <PreventLeaveDialog prevent={hasUnsavedChanges} />
        </div>
    );
};

export default RulesListView;
